'use client'

import React, { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useStytchMember } from '@stytch/nextjs/b2b'
import { useTenant } from '@/components/TenantLayout/Client'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { isAuthPagePath, PATIENT_PATHS } from '@/utilities/paths'
import { Plus } from 'lucide-react'
import PatientNavAccountMenu from '@/components/patient/navigation/PatientNavAccountMenu'
import { FormLinkButton } from '@/components/ui/forms'
import { LanguageDropdown } from '@/components/ui/LanguageDropdown'

export function HeaderPatientRight() {
  const { tenant, user, isOnboarding } = useTenant()
  const pathname = usePathname()
  const isNonAuthPath = !isAuthPagePath(pathname)
  const [canReadStytchSession, setCanReadStytchSession] = useState(false)
  const [hasStytchSession, setHasStytchSession] = useState(false)

  useEffect(() => {
    setCanReadStytchSession(true)
  }, [])

  const hasSession = Boolean(user) || hasStytchSession
  const showNav =
    hasSession && !isOnboarding && isNonAuthPath && pathname !== PATIENT_PATHS.hipaaConsent

  return (
    <div className="flex items-center gap-4 shrink-0">
      {showNav && (
        <div className="hidden sm:contents">
          <FormLinkButton
            href={PATIENT_PATHS.flows.kitRegistrationFirstStep}
            variant="primary"
            size="sm"
            borderRadius="full"
            className="inline-flex items-center gap-2 px-5"
            data-testid="patient-header-register-kit"
            onClick={() =>
              trackEvent('navigation:register_kit_click', { placement: 'header_desktop' })
            }
          >
            Register Kit
            <Plus className="size-4 shrink-0" aria-hidden="true" />
          </FormLinkButton>
        </div>
      )}
      {showNav && <div className="hidden sm:block w-px h-4 bg-border" aria-hidden="true" />}
      {tenant?.slug !== 'client' && <LanguageDropdown />}
      {hasSession && isNonAuthPath && <PatientNavAccountMenu showProfile={showNav} />}
      {canReadStytchSession && !user && (
        <HeaderStytchSessionProbe onSessionChange={setHasStytchSession} />
      )}
    </div>
  )
}

function HeaderStytchSessionProbe({
  onSessionChange,
}: Readonly<{ onSessionChange: (hasSession: boolean) => void }>) {
  const { member, isInitialized } = useStytchMember()

  useEffect(() => {
    if (isInitialized) {
      onSessionChange(Boolean(member))
    }
  }, [isInitialized, member, onSessionChange])

  return null
}
